import React, { useState } from 'react';
import { AlertCircleIcon, CheckCircleIcon, ClockIcon, FileIcon, SearchIcon, UploadIcon } from 'lucide-react';
export function VendorDocuments({
  vendor,
  onViewDocument
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  // Sample document data
  const documents = [{
    id: 'DOC-2023-001',
    name: 'General Liability Insurance',
    type: 'Insurance',
    status: 'Active',
    expirationDate: '2023-12-30',
    uploadedDate: '2022-12-15',
    required: true,
    description: 'General liability insurance certificate with minimum coverage of $1M per occurrence'
  }, {
    id: 'DOC-2023-002',
    name: 'Workers Compensation Insurance',
    type: 'Insurance',
    status: 'Expiring Soon',
    expirationDate: '2023-11-15',
    uploadedDate: '2022-11-10',
    required: true,
    description: 'Workers compensation insurance as required by state law'
  }, {
    id: 'DOC-2023-003',
    name: 'Business License',
    type: 'License',
    status: 'Active',
    expirationDate: '2024-05-20',
    uploadedDate: '2023-05-20',
    required: true,
    description: 'State business license'
  }, {
    id: 'DOC-2023-004',
    name: 'Professional Certification',
    type: 'Certification',
    status: 'Expired',
    expirationDate: '2023-09-01',
    uploadedDate: '2022-09-01',
    required: true,
    description: 'Industry-specific professional certification'
  }, {
    id: 'DOC-2023-005',
    name: 'W-9 Form',
    type: 'Tax Form',
    status: 'Active',
    expirationDate: null,
    uploadedDate: '2023-01-15',
    required: true,
    description: 'Federal W-9 tax form'
  }, {
    id: 'DOC-2023-006',
    name: 'Master Service Agreement',
    type: 'Contract',
    status: 'Active',
    expirationDate: '2025-03-10',
    uploadedDate: '2023-03-10',
    required: true,
    description: 'Signed master service agreement'
  }];
  // Filter documents based on search term and status filter
  const filteredDocuments = documents.filter(doc => {
    const matchesSearch = doc.name.toLowerCase().includes(searchTerm.toLowerCase()) || doc.type.toLowerCase().includes(searchTerm.toLowerCase());
    if (filterStatus === 'all') return matchesSearch;
    return matchesSearch && doc.status.replace(' ', '').toLowerCase() === filterStatus.toLowerCase();
  });
  const getStatusIcon = status => {
    switch (status) {
      case 'Active':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" data-id="element-1140" />;
      case 'Expiring Soon':
        return <ClockIcon className="h-5 w-5 text-yellow-500" data-id="element-1141" />;
      case 'Expired':
        return <AlertCircleIcon className="h-5 w-5 text-red-500" data-id="element-1142" />;
      default:
        return <FileIcon className="h-5 w-5 text-gray-500" data-id="element-1143" />;
    }
  };
  const getStatusColor = status => {
    switch (status) {
      case 'Active':
        return 'bg-green-100 text-green-800';
      case 'Expiring Soon':
        return 'bg-yellow-100 text-yellow-800';
      case 'Expired':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  const formatDate = dateString => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  const getDaysUntilExpiration = expirationDate => {
    if (!expirationDate) return null;
    const today = new Date();
    const expiration = new Date(expirationDate);
    const diffTime = expiration - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };
  return <div data-id="element-1144">
      <div className="mb-8" data-id="element-1145">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-1146">
          Document Management
        </h1>
        <p className="mt-1 text-sm text-gray-500" data-id="element-1147">
          Manage your required documents and certificates
        </p>
      </div>
      {/* Search and Filters */}
      <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-3 sm:space-y-0" data-id="element-1148">
        <div className="relative rounded-md shadow-sm max-w-xs w-full" data-id="element-1149">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1150">
            <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-1151" />
          </div>
          <input type="text" className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md" placeholder="Search documents..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-1152" />
        </div>
        <div className="flex space-x-2" data-id="element-1153">
          <select className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" value={filterStatus} onChange={e => setFilterStatus(e.target.value)} data-id="element-1154">
            <option value="all" data-id="element-1155">All Statuses</option>
            <option value="active" data-id="element-1156">Active</option>
            <option value="expiringsoon" data-id="element-1157">Expiring Soon</option>
            <option value="expired" data-id="element-1158">Expired</option>
          </select>
          <button type="button" className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-1159">
            <UploadIcon className="h-4 w-4 mr-2" data-id="element-1160" />
            Upload New
          </button>
        </div>
      </div>
      {/* Documents List */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md" data-id="element-1161">
        <ul className="divide-y divide-gray-200" data-id="element-1162">
          {filteredDocuments.length > 0 ? filteredDocuments.map(document => {
          const daysUntilExpiration = getDaysUntilExpiration(document.expirationDate);
          return <li key={document.id} data-id="element-1163">
                  <a href="#" onClick={e => {
              e.preventDefault();
              onViewDocument(document);
            }} className="block hover:bg-gray-50" data-id="element-1164">
                    <div className="px-4 py-4 sm:px-6" data-id="element-1165">
                      <div className="flex items-center justify-between" data-id="element-1166">
                        <div className="flex items-center" data-id="element-1167">
                          {getStatusIcon(document.status)}
                          <p className="text-sm font-medium text-indigo-600 truncate ml-2" data-id="element-1168">
                            {document.name}
                          </p>
                        </div>
                        <div className="ml-2 flex-shrink-0 flex" data-id="element-1169">
                          <p className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(document.status)}`} data-id="element-1170">
                            {document.status}
                          </p>
                        </div>
                      </div>
                      <div className="mt-2 sm:flex sm:justify-between" data-id="element-1171">
                        <div className="sm:flex" data-id="element-1172">
                          <p className="flex items-center text-sm text-gray-500" data-id="element-1173">
                            {document.type}
                          </p>
                          {document.required && <p className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0 sm:ml-6" data-id="element-1174">
                              <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800" data-id="element-1175">
                                Required
                              </span>
                            </p>}
                        </div>
                        <div className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0" data-id="element-1176">
                          {document.expirationDate ? <div data-id="element-1177">
                              <p data-id="element-1178">
                                Expires: {formatDate(document.expirationDate)}
                              </p>
                              {daysUntilExpiration !== null && <p className={`text-xs mt-1 ${daysUntilExpiration <= 0 ? 'text-red-600' : daysUntilExpiration <= 30 ? 'text-yellow-600' : 'text-green-600'}`} data-id="element-1179">
                                  {daysUntilExpiration <= 0 ? 'Expired' : `${daysUntilExpiration} days remaining`}
                                </p>}
                            </div> : <p data-id="element-1180">No Expiration</p>}
                        </div>
                      </div>
                    </div>
                  </a>
                </li>;
        }) : <li className="py-6" data-id="element-1181">
              <div className="text-center" data-id="element-1182">
                <FileIcon className="mx-auto h-12 w-12 text-gray-400" data-id="element-1183" />
                <h3 className="mt-2 text-sm font-medium text-gray-900" data-id="element-1184">
                  No documents found
                </h3>
                <p className="mt-1 text-sm text-gray-500" data-id="element-1185">
                  Try adjusting your search or filter criteria.
                </p>
              </div>
            </li>}
        </ul>
      </div>
    </div>;
}